import {
  opportunitiesPipelinesStatusesListOptions,
  opportunitiesPipelinesStatusesListQueryKey,
  opportunitiesStatusesCreateMutation,
  opportunitiesStatusesDestroyMutation,
  opportunitiesStatusesPartialUpdateMutation,
  opportunitiesStatusesRetrieveOptions,
  PaginatedReturnOpportunityStatusListReadable,
  ReturnOpportunityPipelineNomenclatorReadable,
  ReturnOpportunityStatusReadable
} from '@crm/api'
import { ConfirmModal, DndTable } from '@crm/components'
import { FormNomenclaturesOpportunitiesPipelinesStatutes } from '@crm/features/nomenclatures/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters } from '@crm/utils'
import { DayLabel, TableActions, TitleSection } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Form, Modal } from 'antd'

import { Add, Edit, Trash } from 'iconsax-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useParams } from 'react-router-dom'

import type { MenuProps, TableColumnsType } from 'antd'

export const NomenclaturesOpportunityPipelineStatus = () => {
  const { t } = useLocales()
  const { id } = useParams()
  const params = useQueryParams()
  const sorter = useTableSorter()
  const { resetPagination } = useResetPagination()

  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { notification } = App.useApp()

  const [form] = Form.useForm()

  const [data, setData] =
    useState<PaginatedReturnOpportunityStatusListReadable>()

  const [isCreate, setIsCreate] = useState(false)
  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()
  const [sorted, setSorted] = useState(false)

  const { data: statusList, isLoading } = useQuery(
    opportunitiesPipelinesStatusesListOptions({
      path: {
        pipeline_id: Number(id)
      },
      query: {
        ...defaultFilters,
        ...params
      }
    })
  )

  const { data: status, isLoading: isLoadingStatus } = useQuery({
    ...opportunitiesStatusesRetrieveOptions({
      path: {
        id: Number(editItem)
      }
    }),
    enabled: !!editItem
  })

  useEffect(() => {
    if (statusList) {
      setData(statusList)
    }
  }, [statusList])

  useEffect(() => {
    if (status) {
      form.setFieldsValue(status)
    }
  }, [status])

  const { mutate: updateMutate, isPending: isPendingUpdate } = useMutation({
    ...opportunitiesStatusesPartialUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([
        opportunitiesPipelinesStatusesListQueryKey({
          path: {
            pipeline_id: Number(id)
          }
        })
      ])
      onCloseModal()
    }
  })

  const { mutate: createMutate, isPending: isPendingCreate } = useMutation({
    ...opportunitiesStatusesCreateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([
        opportunitiesPipelinesStatusesListQueryKey({
          path: { pipeline_id: Number(id) }
        })
      ])
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
      onCloseModal()
    }
  })

  const { mutate: deleteMutate } = useMutation({
    ...opportunitiesStatusesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      resetPagination()
      invalidateQueries([
        opportunitiesPipelinesStatusesListQueryKey({
          path: {
            pipeline_id: Number(id)
          }
        })
      ])
      setDeleteItem(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  useEffect(() => {
    if (data?.results && sorted) {
      setData(
        (prevState) =>
          ({
            ...prevState,
            results: data?.results?.map(({ id, pipeline, ...params }, i) => {
              if (
                i + 1 !==
                statusList?.results?.find((item) => item.id === id)?.order
              ) {
                updateMutate({
                  path: { id },
                  body: {
                    ...params,
                    pipeline:
                      pipeline?.id as unknown as ReturnOpportunityPipelineNomenclatorReadable,
                    order: i + 1
                  }
                })

                return { id, ...params, order: i + 1 }
              }

              return { id, ...params }
            })
          } as PaginatedReturnOpportunityStatusListReadable)
      )
      setSorted(false)
    }
  }, [data, sorted, statusList?.results, updateMutate])

  const onDelete = () => {
    if (deleteItem) {
      deleteMutate({ path: { id: deleteItem } })
    }
  }

  const onCancelDelete = () => setDeleteItem(undefined)

  const onCloseModal = () => {
    setEditItem(undefined)
    setIsCreate(false)
    form.resetFields()
  }

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        icon: <Edit size={20} />,
        key: 'edit',
        onClick: () => setEditItem(id)
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        key: 'delete',
        icon: <Trash size={20} />,
        danger: true,
        onClick: () => setDeleteItem(id)
      }
    ],
    [t]
  )

  const columns: TableColumnsType<ReturnOpportunityStatusReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true
      },
      {
        title: t('nomenclature.code'),
        dataIndex: 'code',
        key: 'code',
        sorter: true
      },
      {
        title: t('nomenclature.order'),
        dataIndex: 'order',
        key: 'order',
        sorter: true
      },
      {
        title: t('nomenclature.is_default'),
        key: 'is_default',
        sorter: true,
        render: ({ is_default }) =>
          is_default ? t('buttons.yes') : t('buttons.no')
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        render: ({ id }) => <TableActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  const onSortEnd = (
    action: (
      state: ReturnOpportunityStatusReadable[]
    ) => ReturnOpportunityStatusReadable[]
  ) => {
    setData((prev) => {
      const results = action(prev?.results || [])

      return {
        ...prev,
        results: results || prev?.results
      } as PaginatedReturnOpportunityStatusListReadable
    })

    setSorted(true)
  }

  const dndItems = data?.results?.map(({ id }) => id) || []

  const onSubmit = async () => {
    const values = await form.validateFields()

    if (editItem) {
      updateMutate({
        path: { id: editItem },
        body: { ...values, pipeline: Number(id) }
      })
    } else {
      createMutate({
        body: { ...values, pipeline: Number(id) }
      })
    }
  }

  return (
    <>
      <TitleSection title={t(`nomenclature.opportunities_statuses`)} />

      <DndTable
        onDragEnd={onSortEnd}
        rowKey="id"
        items={dndItems}
        data={data}
        columns={columns}
        loading={isLoading}
        onChange={(_, __, result) => sorter(result)}
        rightChildren={
          <Button
            type="primary"
            icon={<Add size={20} />}
            onClick={() => setIsCreate(true)}
          >
            {t('nomenclature.add_status')}
          </Button>
        }
        showSorterTooltip={false}
        bordered
      />

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <Modal
        open={!!(editItem ?? isCreate)}
        onCancel={onCloseModal}
        loading={isLoadingStatus}
        okButtonProps={{
          htmlType: 'submit',
          loading: isPendingUpdate || isPendingCreate,
          onClick: onSubmit
        }}
      >
        <Form form={form} layout="vertical">
          <FormNomenclaturesOpportunitiesPipelinesStatutes
            pipelineId={Number(id)}
          />
        </Form>
      </Modal>
    </>
  )
}
